import { useState, useCallback, useRef, useEffect } from 'react';
import type { Editor } from '@tiptap/react';
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  ListChecks,
  Quote,
  CodeSquare,
  Minus,
  Table,
  Link,
  ChevronDown,
  Unlink,
  SquareCheckBig,
  ChevronsUpDown,
  StickyNote,
  Blocks,
} from 'lucide-react';
import { apiPost } from '../../lib/api';
import { SECTION_TYPES } from './AutoSectionExtension';

interface EditorToolbarProps {
  editor: Editor | null;
  meetingInstanceId?: string;
  onTaskCreated?: () => void;
  onNoteCreated?: () => void;
}

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

function ToolbarButton({ onClick, isActive, disabled, title, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`
        p-1 rounded-md transition-colors duration-100
        disabled:opacity-30 disabled:cursor-not-allowed
        ${isActive
          ? 'bg-accent/20 text-accent-hover'
          : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
        }
      `}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <div className="w-px h-4 bg-border mx-0.5" />;
}

export function EditorToolbar({ editor, meetingInstanceId, onTaskCreated, onNoteCreated }: EditorToolbarProps) {
  const [headingOpen, setHeadingOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskCreating, setTaskCreating] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteCreating, setNoteCreating] = useState(false);
  const [autoSectionOpen, setAutoSectionOpen] = useState(false);
  const headingRef = useRef<HTMLDivElement>(null);
  const linkRef = useRef<HTMLDivElement>(null);
  const autoSectionRef = useRef<HTMLDivElement>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);
  const taskRef = useRef<HTMLDivElement>(null);
  const taskInputRef = useRef<HTMLInputElement>(null);
  const noteRef = useRef<HTMLDivElement>(null);
  const noteInputRef = useRef<HTMLInputElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (headingRef.current && !headingRef.current.contains(e.target as Node)) {
        setHeadingOpen(false);
      }
      if (linkRef.current && !linkRef.current.contains(e.target as Node)) {
        setLinkOpen(false);
        setLinkUrl('');
      }
      if (taskRef.current && !taskRef.current.contains(e.target as Node)) {
        setTaskOpen(false);
        setTaskTitle('');
      }
      if (noteRef.current && !noteRef.current.contains(e.target as Node)) {
        setNoteOpen(false);
        setNoteTitle('');
      }
      if (autoSectionRef.current && !autoSectionRef.current.contains(e.target as Node)) {
        setAutoSectionOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Focus link input when dropdown opens
  useEffect(() => {
    if (linkOpen && linkInputRef.current) {
      const currentUrl = editor?.getAttributes('link').href || '';
      setLinkUrl(currentUrl);
      // Small delay to let the dropdown render
      requestAnimationFrame(() => linkInputRef.current?.focus());
    }
  }, [linkOpen, editor]);

  const setHeading = useCallback(
    (level: 1 | 2 | 3) => {
      editor?.chain().focus().toggleHeading({ level }).run();
      setHeadingOpen(false);
    },
    [editor],
  );

  const applyLink = useCallback(() => {
    if (!editor) return;
    const trimmed = linkUrl.trim();
    if (trimmed === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      const url = /^https?:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    setLinkOpen(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  const removeLink = useCallback(() => {
    editor?.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkOpen(false);
    setLinkUrl('');
  }, [editor]);

  const insertTable = useCallback(() => {
    editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  // Pre-fill task title from selected text when popover opens
  useEffect(() => {
    if (taskOpen && taskInputRef.current) {
      const selection = editor?.state?.selection;
      if (selection && !selection.empty) {
        const selectedText = editor.state.doc.textBetween(selection.from, selection.to, ' ');
        if (selectedText.trim()) {
          setTaskTitle(selectedText.trim());
        }
      }
      requestAnimationFrame(() => taskInputRef.current?.focus());
    }
  }, [taskOpen, editor]);

  const createTask = useCallback(async () => {
    if (!editor || !taskTitle.trim()) return;
    setTaskCreating(true);
    try {
      const created = await apiPost<{ id: string; title: string }>('/tasks', {
        title: taskTitle.trim(),
        source_meeting_id: meetingInstanceId,
      });

      // Insert a task reference node as a styled inline element
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'taskReference',
          attrs: {
            id: created.id,
            title: created.title,
            checked: false,
          },
        })
        .run();

      setTaskOpen(false);
      setTaskTitle('');
      onTaskCreated?.();
    } catch (err) {
      console.error('Failed to create task:', err);
    } finally {
      setTaskCreating(false);
    }
  }, [editor, taskTitle, meetingInstanceId, onTaskCreated]);

  // Pre-fill note title from selected text when popover opens
  useEffect(() => {
    if (noteOpen && noteInputRef.current) {
      const selection = editor?.state?.selection;
      if (selection && !selection.empty) {
        const selectedText = editor.state.doc.textBetween(selection.from, selection.to, ' ');
        if (selectedText.trim()) {
          setNoteTitle(selectedText.trim());
        }
      }
      requestAnimationFrame(() => noteInputRef.current?.focus());
    }
  }, [noteOpen, editor]);

  const createNote = useCallback(async () => {
    if (!editor || !noteTitle.trim()) return;
    setNoteCreating(true);
    try {
      const created = await apiPost<{ id: string; title: string }>('/notes', {
        title: noteTitle.trim(),
        source_meeting_id: meetingInstanceId,
      });

      // Insert a note reference node as a styled inline element
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'noteReference',
          attrs: {
            id: created.id,
            title: created.title,
          },
        })
        .run();

      setNoteOpen(false);
      setNoteTitle('');
      onNoteCreated?.();
    } catch (err) {
      console.error('Failed to create note:', err);
    } finally {
      setNoteCreating(false);
    }
  }, [editor, noteTitle, meetingInstanceId, onNoteCreated]);

  if (!editor) return null;

  // Determine active heading for the dropdown label
  const activeHeadingLevel = [1, 2, 3].find((l) =>
    editor.isActive('heading', { level: l }),
  ) as 1 | 2 | 3 | undefined;

  return (
    <div className="flex items-center gap-0.5 px-2 py-1 border-b border-border bg-bg-secondary overflow-x-auto sm:flex-wrap sm:overflow-x-visible mobile-toolbar-scroll">
      {/* Bold / Italic / Strikethrough / Code */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        title="Bold (Ctrl+B)"
      >
        <Bold size={16} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        title="Italic (Ctrl+I)"
      >
        <Italic size={16} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive('strike')}
        title="Strikethrough"
      >
        <Strikethrough size={16} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCode().run()}
        isActive={editor.isActive('code')}
        title="Inline code"
      >
        <Code size={16} />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Heading dropdown */}
      <div ref={headingRef} className="relative">
        <button
          type="button"
          onClick={() => setHeadingOpen((p) => !p)}
          title="Heading"
          className={`
            flex items-center gap-0.5 px-2 py-1 rounded-md text-sm transition-colors duration-100
            ${activeHeadingLevel
              ? 'bg-accent/20 text-accent-hover'
              : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
            }
          `}
        >
          <span className="font-medium text-xs">
            {activeHeadingLevel ? `H${activeHeadingLevel}` : 'H'}
          </span>
          <ChevronDown size={12} />
        </button>

        {headingOpen && (
          <div className="absolute top-full left-0 mt-1 z-50 bg-bg-card border border-border rounded-lg shadow-lg py-1 min-w-[140px]">
            <button
              type="button"
              onClick={() => setHeading(1)}
              className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors
                ${editor.isActive('heading', { level: 1 })
                  ? 'text-accent-hover bg-accent/10'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                }`}
            >
              <Heading1 size={16} /> Heading 1
            </button>
            <button
              type="button"
              onClick={() => setHeading(2)}
              className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors
                ${editor.isActive('heading', { level: 2 })
                  ? 'text-accent-hover bg-accent/10'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                }`}
            >
              <Heading2 size={16} /> Heading 2
            </button>
            <button
              type="button"
              onClick={() => setHeading(3)}
              className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors
                ${editor.isActive('heading', { level: 3 })
                  ? 'text-accent-hover bg-accent/10'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                }`}
            >
              <Heading3 size={16} /> Heading 3
            </button>
          </div>
        )}
      </div>

      <ToolbarDivider />

      {/* Lists */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        title="Bullet list"
      >
        <List size={16} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        title="Numbered list"
      >
        <ListOrdered size={16} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleTaskList().run()}
        isActive={editor.isActive('taskList')}
        title="Task list"
      >
        <ListChecks size={16} />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Block elements */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
        title="Blockquote"
      >
        <Quote size={16} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive('codeBlock')}
        title="Code block"
      >
        <CodeSquare size={16} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Horizontal rule"
      >
        <Minus size={16} />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Table */}
      <ToolbarButton
        onClick={insertTable}
        title="Insert table"
      >
        <Table size={16} />
      </ToolbarButton>

      {/* Link */}
      <div ref={linkRef} className="relative">
        <ToolbarButton
          onClick={() => setLinkOpen((p) => !p)}
          isActive={editor.isActive('link')}
          title="Link"
        >
          <Link size={16} />
        </ToolbarButton>

        {linkOpen && (
          <div className="absolute top-full left-0 mt-1 z-50 bg-bg-card border border-border rounded-lg shadow-lg p-2 min-w-[280px]">
            <div className="flex items-center gap-1.5">
              <input
                ref={linkInputRef}
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyLink();
                  }
                  if (e.key === 'Escape') {
                    setLinkOpen(false);
                    setLinkUrl('');
                    editor.commands.focus();
                  }
                }}
                placeholder="https://example.com"
                className="flex-1 px-2.5 py-1.5 text-sm rounded-md bg-bg-input border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
              />
              <button
                type="button"
                onClick={applyLink}
                className="px-2.5 py-1.5 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors"
              >
                Apply
              </button>
              {editor.isActive('link') && (
                <button
                  type="button"
                  onClick={removeLink}
                  title="Remove link"
                  className="p-1.5 rounded-md text-text-muted hover:text-danger hover:bg-bg-hover transition-colors"
                >
                  <Unlink size={14} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Collapsible Section */}
      <ToolbarButton
        onClick={() => editor.chain().focus().setDetails().run()}
        title="Collapsible section"
      >
        <ChevronsUpDown size={16} />
      </ToolbarButton>

      {/* Auto Section insert */}
      <div ref={autoSectionRef} className="relative">
        <ToolbarButton
          onClick={() => setAutoSectionOpen((p) => !p)}
          title="Insert auto-section"
        >
          <Blocks size={16} />
        </ToolbarButton>

        {autoSectionOpen && (
          <div className="absolute top-full left-0 mt-1 z-50 bg-bg-card border border-border rounded-lg shadow-lg py-1 min-w-[220px]">
            {SECTION_TYPES.map(section => (
              <button
                key={section.value}
                type="button"
                onClick={() => {
                  editor.chain().focus().insertContent({
                    type: 'autoSection',
                    attrs: { sectionType: section.value },
                  }).run();
                  setAutoSectionOpen(false);
                }}
                className="w-full text-left px-3 py-1.5 text-sm flex flex-col gap-0.5 transition-colors text-text-secondary hover:text-text-primary hover:bg-bg-hover"
              >
                <span className="font-medium">{section.label}</span>
                <span className="text-[10px] text-text-muted">{section.description}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <ToolbarDivider />

      {/* Create Task */}
      <div ref={taskRef} className="relative">
        <ToolbarButton
          onClick={() => setTaskOpen((p) => !p)}
          title="Create task"
        >
          <SquareCheckBig size={16} />
        </ToolbarButton>

        {taskOpen && (
          <div className="absolute top-full right-0 mt-1 z-50 bg-bg-card border border-border rounded-lg shadow-lg p-2 min-w-[300px]">
            <p className="text-xs text-text-muted mb-1.5 px-0.5">Create a new task</p>
            <div className="flex items-center gap-1.5">
              <input
                ref={taskInputRef}
                type="text"
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    createTask();
                  }
                  if (e.key === 'Escape') {
                    setTaskOpen(false);
                    setTaskTitle('');
                    editor.commands.focus();
                  }
                }}
                placeholder="Task title..."
                disabled={taskCreating}
                className="flex-1 px-2.5 py-1.5 text-sm rounded-md bg-bg-input border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent disabled:opacity-50"
              />
              <button
                type="button"
                onClick={createTask}
                disabled={taskCreating || !taskTitle.trim()}
                className="px-2.5 py-1.5 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {taskCreating ? '...' : 'Create'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Note */}
      <div ref={noteRef} className="relative">
        <ToolbarButton
          onClick={() => setNoteOpen((p) => !p)}
          title="Create note"
        >
          <StickyNote size={16} />
        </ToolbarButton>

        {noteOpen && (
          <div className="absolute top-full right-0 mt-1 z-50 bg-bg-card border border-border rounded-lg shadow-lg p-2 min-w-[300px]">
            <p className="text-xs text-text-muted mb-1.5 px-0.5">Create a new note</p>
            <div className="flex items-center gap-1.5">
              <input
                ref={noteInputRef}
                type="text"
                value={noteTitle}
                onChange={(e) => setNoteTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    createNote();
                  }
                  if (e.key === 'Escape') {
                    setNoteOpen(false);
                    setNoteTitle('');
                    editor.commands.focus();
                  }
                }}
                placeholder="Note title..."
                disabled={noteCreating}
                className="flex-1 px-2.5 py-1.5 text-sm rounded-md bg-bg-input border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent disabled:opacity-50"
              />
              <button
                type="button"
                onClick={createNote}
                disabled={noteCreating || !noteTitle.trim()}
                className="px-2.5 py-1.5 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {noteCreating ? '...' : 'Create'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
